import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { ItemCardCustomization, SelectableItemPortion, ItemCardCustomizationType, ItemCardSelectionInfoKind, CustomerProfile, DietaryTagUI, MenuItemMetadata } from '@/models';
import { formatPortion, formatCustomization } from '@/services/meal-builder/utils';
import { sortFn, UniqueValue, capitalizeEveryWord, isCustomerCanadian } from '@/helpers';
import { ErrorEvent } from '@/services/analytics/events';
import CmgMealBuilderItemSelector
  from '@/components/meal-builder/CmgMealBuilderItemSelector/cmg-meal-builder-item-selector';

@Component({
  name: 'CmgMealBuilderItemSelectorCard',
})
export default class CmgMealBuilderItemSelectorCard extends Vue {
  closeCustomizationsListener?: (event: MouseEvent) => void;
  touchStartY?: number;

  @Prop() private parent!: CmgMealBuilderItemSelector;

  @Prop({ type: String })
  thumbnailImageUrl?: string;

  @Prop({ type: String })
  name?: string;

  @Prop({ type: String })
  price?: string;

  @Prop({ type: String })
  calories?: string;

  @Prop({ type: Array })
  customizations?: ItemCardCustomization[];

  @Prop({ type: String })
  portion?: SelectableItemPortion;

  @Prop({ type: Number })
  quantity?: number;

  @Prop({ default: false, type: Boolean })
  selected!: boolean;

  @Prop({ required: false, type: String })
  type!: ItemCardSelectionInfoKind;

  @Prop({ type: Object })
  message?: UniqueValue<string>;

  @Prop({ default: false, type: Boolean })
  customizing?: boolean;

  @Prop({ default: false, type: Boolean })
  eventsDisabled?: boolean;

  @Prop({ type: String })
  promo?: string;

  @Prop({ type: String })
  tagline?: string;

  @Prop({ default: 'CUSTOMZIE', type: String })
  customizeText!: string;

  @Prop({ default: false, type: Boolean })
  isGroupedItem!: boolean;

  @Prop({ default: true, type: Boolean })
  isItemAvailable?: boolean;

  @Prop({ default: true, type: Boolean })
  eligibleForDelivery?: boolean;

  // unit test prop
  @Prop({ default: false, type: Boolean })
  showMessageAlways!: boolean;

  @Prop({ type: Boolean })
  isItemExclusive!: boolean;

  @Prop({ required: false, type: String })
  selectedText?: string;

  @Prop({ required: false, type: Object })
  metadata?: MenuItemMetadata;

  @Prop({ required: false, type: String })
  rewardsText?: string; // Text from the 'rewards' metadata tag. Used for the rewards-exclusive pill

  @Prop({ required: false, type: Boolean })
  isUniversalCard?: boolean;

  get customization(): ItemCardCustomization | undefined {
    return this.customizations?.find(c => c.selected);
  }

  get hasCustomization(): boolean {
    return !!this.customization || this.isGroupedItem;
  }

  get customizationText(): string | undefined {
    return this.selectedText || (this.customization && formatCustomization(this.customization));
  }

  get visibleCustomizations(): ItemCardCustomization[] {
    const sortedCustomizations = (this.customizations ?? [])
      .filter(c => !c.hidden)
      .sort(sortFn(c => (c.type == ItemCardCustomizationType.Customization ? c.customization.id : 0)));

    return this.isGroupedItem
      ? sortedCustomizations
      : [
          ...sortedCustomizations,
          {
            type: ItemCardCustomizationType.Normal,
            text: 'Normal',
            selected: !this.hasCustomization,
            iconText: null,
            hidden: false,
          },
        ];
  }

  get hasAvailableCustomizations(): boolean {
    return !!this.customizations?.some(c => !c.hidden);
  }

  get formattedPortion(): string | undefined {
    if (!this.portion) {
      return;
    }

    return formatPortion(this.portion);
  }

  get itemUnavailableText(): string {
    return !this.isItemAvailable ? 'TEMPORARILY UNAVAILABLE' : `Sorry, can't be delivered`;
  }

  get messageArrowsImageUrl(): string {
    return this.$appStore.state.cms.images.icons.swipeArrows.default;
  }

  get isEligibleItem(): boolean {
    return this.$services.order.isPickingUp() || !!this.eligibleForDelivery;
  }

  get itemName(): string {
    return this.name || "";
  }

  // Used only for automated testing purposes
  get dataQATitle(): string {
    return capitalizeEveryWord(this.itemName);
  }

  get energyUnitByCountry(): string {
    return this.$services.config.getEnergyUnitByCountry() ?? 'cal';
  }

  get ariaLabelForSelectorCard(): string {
    let result = this.selected ? 'You have selected ' : 'You can select ';
    const calories = this.calories ? `${this.calories}${this.energyUnitByCountry}` : '';
    result += this.itemName ? `${this.itemName} ${this.price ?? ''} ${calories}` : this.dataQATitle;

    return result;
  }

  get showRewardsExclusivePill(): boolean {
    return (this.rewardsText && !this.isItemExclusive) || (this.isEnrolledInLoyalty && this.isItemExclusive && this.isEligibleItem);
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isCustomerCanadian(): boolean | null {
    return isCustomerCanadian(this.customer, this.$appStore.state);
  }

  get showItem(): boolean {
    return this.isEnrolledInLoyalty || !this.isItemExclusive;
  }

  get tagLine(): string {
    return this.tagline || '';
  }

  get ariaLabel(): string {
    return `${this.selected ? 'You have selected' : 'You can select'} ${this.itemName ? this.itemName + ' ' : ''}${
      this.tagLine && this.isItemAvailable && this.isEligibleItem ? this.tagLine + ' ' : ''
    }${this.price ? this.price + ' ' : ''}${this.calories ? this.calories + this.energyUnitByCountry : ''}`;
  }

  get dietaryTags(): DietaryTagUI[] {
    const tagIntersection = this.metadata?.dietaryTags?.filter(x => this.$appStore.state.customer.profile?.dietaryTags?.includes(x)) || [];

    // Only one badge from each group
    const tagBadges: {
      [key: string]: DietaryTagUI;
    } = {};
    tagIntersection
      .map(tag => this.$appStore.state.menu.metadata?.dietaryTagsLookup[tag])
      .filter(t => t != undefined)
      .forEach(t => (tagBadges[t!.tagGroupId] = t!));

    return Object.values(tagBadges);
  }

  mounted() {
    if (document.location.search && new URLSearchParams(document.location.search).get("filling")) {
      this.preselectProtein();
    }
  }

  // Ensure rewards exclusive pill is loaded before preselecting protein
  onPillLoaded() {
    if (document.location.search && new URLSearchParams(document.location.search).get("filling")) {
      this.preselectProtein();
    }
  }

  preselectProtein() {
    const cmgIdParam = new URLSearchParams(document.location.search)?.get("filling");
    const element = this.$el as HTMLElement

    if (cmgIdParam && element.dataset?.qaItemId === cmgIdParam && !(this.$refs.overlay as HTMLElement).classList.contains('selected')) {
      const cardSelectionOverlay = this.$refs.selectionOverlay as HTMLElement;
      this.$nextTick(() => {
        if (this.isItemAvailable) {
          cardSelectionOverlay.click();
        }
      });
    }
  }

  onSelect<TArgs>(args?: TArgs) {
    if (this.eventsDisabled) {
      return;
    }

    if (this.customizing) {
      this.requestCustomizationState(false);
    }

    this.$emit('select', args);
  }

  onSelectQuantity(quantity: number, event: any) {
    this.onSelect(quantity);
    event.stopPropagation();
    event.preventDefault();
  }

  displayUniversalCardDetails() {
    if (this.isUniversalCard) {
      const card = this.$el as HTMLElement;
      const facContainer = this.$refs.facContainer as HTMLElement;
      const itemDetails = this.$refs.itemDetails as HTMLElement;

      if (facContainer && itemDetails) {
        facContainer.classList.remove('hidden');
        itemDetails.classList.add('hidden');
        if (!this.$services.mealBuilder.getErrorEventSent('UniversalMealBuilderErrorEvent')) {
          this.$services.analytics.send(new ErrorEvent('builder','Find a Chipotle for pricing and availability', 'item-selection'));
          this.$services.mealBuilder.setErrorEventSent('UniversalMealBuilderErrorEvent', true);
        }
        card.style.backgroundImage = '';
      }
    }
  }

  hideUniversalCardDetails() {
    if (this.isUniversalCard) {
      const card = this.$el as HTMLElement;
      const facContainer = this.$refs.facContainer as HTMLElement;
      const itemDetails = this.$refs.itemDetails as HTMLElement;

      if (facContainer && itemDetails) {
        facContainer.classList.add('hidden');
        itemDetails.classList.remove('hidden');

        card.style.backgroundImage = `url(${this.thumbnailImageUrl})`;
      }
    }
  }

  handleTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches[0].clientY;
  }

  toggleMobileUniversalCard(event: TouchEvent) {
    const facContainer = this.$refs.facContainer as HTMLElement;
    const touch = event.changedTouches[0];

    if(!facContainer.classList.contains('hidden')) {
      this.hideUniversalCardDetails();
      return;
    }

    if (this.isUniversalCard && Math.abs(this.touchStartY! - touch.clientY) < 10 && facContainer.classList.contains('hidden')) {
      this.touchStartY = 0;
      this.displayUniversalCardDetails();

      // auto hide card details after 4 seconds
      setTimeout(() => {
        this.hideUniversalCardDetails();
      }, 4000);
    } else {
      this.touchStartY = 0;
      return;
    }
  }

  onSelectCard(event: PointerEvent) {
    if (this.isUniversalCard && event.pointerType != 'touch') {
      if (!(this.$refs.itemDetails as HTMLElement).classList.contains('hidden')) {
        this.displayUniversalCardDetails(); 
      } else {
        this.hideUniversalCardDetails();
      }
    } else {
      switch (this.type) {
        case ItemCardSelectionInfoKind.Single:
          return this.onSelect(!this.selected);
        case ItemCardSelectionInfoKind.Quantity:
          if (this.selected) {
            return;
          }
  
          return this.onSelect(1);
      }
    }
  }

  onClickFAC() {
    this.hideUniversalCardDetails();
    this.$services.ui.setModalVisibility('findAChipotle', true);
  }

  startCustomizing(event: any) {
    this.requestCustomizationState(true);
    Vue.nextTick(() => {
      setTimeout(() => {
        const focusFirstelement = document.querySelectorAll(
          '.card .customizations-desktop .customization-options.customizing .customization-option:nth-child(2) .text',
        )[0] as HTMLElement;
        if (focusFirstelement) {
          focusFirstelement.focus();
        }
      });
    });

    event?.stopPropagation();
    event?.preventDefault();
  }

  stopCustomizing() {
    this.requestCustomizationState(false);
  }

  onSelectCustomization(customization: ItemCardCustomization, event: any) {
    if (this.eventsDisabled) {
      return;
    }

    this.stopCustomizing();

    this.$emit('select-customization', customization);
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  requestCustomizationState(customizing: boolean) {
    if (this.eventsDisabled) {
      return;
    }

    this.$emit('request-customization-state', customizing);
  }
}
